import { useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Upload, FileSpreadsheet, Loader2, HelpCircle } from "lucide-react";
import { toast } from "sonner";

const DATASETS = [
  { value: "cobranzas", label: "Sección 1 · Cobranzas", cols: "cliente, vendedor, fecha_acreditacion, monto_usd" },
  { value: "cuentas", label: "Sección 2 · Cuentas por Recibir", cols: "cliente, valor_vencido, valor_a_vencer, total_cartera, linea_total, pct_linea_facturado, valor_linea_usd, pct_utilizacion" },
  { value: "pagares", label: "Secciones 3-4 · Pagarés", cols: "cliente, fecha_emision, fecha_vencimiento, valor_usd, estado" },
];

export default function ImportModal({ onImported, vendedores = [], periodo }) {
  const [dataset, setDataset] = useState("cobranzas");
  const [vendedor, setVendedor] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [ambig, setAmbig] = useState(null); // { archivo, opciones }
  const [pick, setPick] = useState("");

  const current = DATASETS.find((d) => d.value === dataset);

  const submit = async (overrideVend) => {
    const vend = ((overrideVend ?? vendedor) || "").trim();
    if (!vend) return toast.error("Indique el vendedor de este informe");
    if (!file) return toast.error("Seleccione un archivo Excel, CSV o PDF");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("vendedor", vend);
      fd.append("periodo", periodo || "");
      const { data } = await api.post(`/import/${dataset}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(data.message);
      setOpen(false);
      setFile(null);
      setVendedor("");
      setAmbig(null);
      setPick("");
      onImported();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 409 && detail?.code === "ambiguo") {
        setAmbig({ archivo: detail.archivo, opciones: detail.opciones || [] });
        setPick("");
        toast.message(detail.message || "¿A cuál vendedor corresponde?");
      } else if (status === 404 && detail?.code === "no_encontrado") {
        setAmbig({ archivo: detail.archivo, opciones: detail.opciones || [], notFound: true });
        setPick("");
        toast.error(detail.message || "No se encontró el vendedor");
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Error al importar archivo"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setAmbig(null); setPick(""); } }}>
      <DialogTrigger asChild>
        <Button data-testid="import-excel-modal-trigger" variant="outline" className="h-9 border-emerald-200 text-emerald-800 hover:bg-emerald-50">
          <Upload className="h-4 w-4 mr-1.5" /> Importar Excel/CSV/PDF
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-emerald-700" /> Importar datos
          </DialogTitle>
          <DialogDescription>
            Cargue un archivo Excel (.xlsx), CSV o PDF con tablas para un vendedor. Al reimportar el mismo vendedor y módulo se reemplazan solo sus datos.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div data-testid="import-periodo-banner" className="rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-sm text-emerald-800">
            Estás cargando el informe para el mes: <strong>{periodo || "—"}</strong>. Se guarda vinculado a este mes sin borrar los meses anteriores.
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Vendedor de este informe</label>
            <input
              list="vendedores-list"
              data-testid="import-vendedor-input"
              value={vendedor}
              onChange={(e) => { setVendedor(e.target.value); setAmbig(null); }}
              placeholder="Ej: R. González"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <datalist id="vendedores-list">
              {vendedores.map((v) => <option key={v} value={v} />)}
            </datalist>
            <p className="text-[11px] text-slate-500">Cargue un archivo por vendedor. Al reimportar el mismo vendedor se reemplazan solo sus datos.</p>
          </div>

          {ambig && (
            <div data-testid="import-ambig-panel" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 space-y-2">
              <p className="text-sm text-amber-900 flex items-start gap-1.5">
                <HelpCircle className="h-4 w-4 mt-0.5 shrink-0" />
                {ambig.notFound
                  ? <span>No se encontró <strong>{ambig.archivo}</strong>. Elegí uno de tus vendedores o registralo.</span>
                  : <span>El archivo dice <strong>{ambig.archivo}</strong>: ¿a cuál vendedor corresponde?</span>}
              </p>
              <Select value={pick} onValueChange={setPick}>
                <SelectTrigger data-testid="import-ambig-select"><SelectValue placeholder="Elegí el vendedor…" /></SelectTrigger>
                <SelectContent>
                  {ambig.opciones.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button data-testid="import-ambig-confirm" size="sm" disabled={!pick || loading}
                onClick={() => submit(pick)} className="w-full bg-amber-600 hover:bg-amber-700">
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Confirmar vendedor y cargar
              </Button>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Módulo destino</label>
            <Select value={dataset} onValueChange={setDataset}>
              <SelectTrigger data-testid="import-dataset-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DATASETS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="text-xs bg-slate-50 border border-slate-200 rounded-lg p-3 text-slate-600">
            <span className="font-semibold text-slate-700">Columnas esperadas:</span>
            <div className="font-mono mt-1 text-[11px]">{current.cols}</div>
            <div className="mt-2 text-[11px] text-emerald-700">
              📄 Los PDF se procesan automáticamente con IA para extraer las tablas del informe.
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Archivo</label>
            <input
              type="file"
              data-testid="import-file-input"
              accept=".xlsx,.xls,.csv,.pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-emerald-700 file:text-white file:font-medium hover:file:bg-emerald-800 cursor-pointer"
            />
          </div>

          <Button data-testid="import-submit-button" onClick={() => submit()} disabled={loading} className="w-full bg-emerald-700 hover:bg-emerald-800">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Importar datos
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
